import { validateState } from "@hagerty/self-service-components/api/utils";
import { formatDateISO, getStateName, PolicyDetails } from "shared";
import { useDownloadIdCard } from "../../Insurance/useDownloadIdCard";
import { IdCard } from "../../../svg";
import { useDialog } from "../../Dialog";
import { useEffect, useState } from "react";
import { CompOnlyDialog } from "../../Insurance/CompOnlyDialog";
import { DownloadDocumentErrorAlert } from "../../Insurance/DownloadDocumentErrorAlert";
import { getVehiclesLabel } from "../../../utils/vehicleUtils";
import { mt } from "../../../utils/tracking";
import { PopoverTile } from "../../PopoverTile";
import { useFeatureFlag, useUrls } from "../../../app/Providers/AppContext";
import { Tile } from "../../Tile";

import styles from "./PolicyTiles.module.scss";

type IdCardTileProps = {
  policy: PolicyDetails;
};

const getIdCardTagActionName = (suffix = ""): string => `ID Card${suffix}`;

export const IdCardTile = ({ policy }: IdCardTileProps) => {
  const { ID_CARD_PAGE } = useFeatureFlag();
  const urls = useUrls();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { policyNumber, country } = policy;
  const vehicleNamesLabel = getVehiclesLabel(policy.vehicles);
  const futureVersion = policy.futureVersion;
  const hasFutureVersion = !!futureVersion;
  const showFutureDropdown = hasFutureVersion && policy.hasIdCard;

  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false);
  const compOnlyDialog = useDialog();

  const {
    download: downloadIdCard,
    isDownloading: isDownloadingIdCard,
    error: downloadIdCardError,
  } = useDownloadIdCard(policyNumber, country);

  const {
    download: downloadFutureIdCard,
    isDownloading: isDownloadingFutureIdCard,
    error: downloadFutureIdCardError,
  } = hasFutureVersion
    ? useDownloadIdCard(futureVersion.policyNumber, country)
    : { download: () => undefined, isDownloading: false, error: null };

  useEffect(() => {
    setIsErrorAlertOpen(!!(downloadIdCardError || downloadFutureIdCardError));
  }, [downloadIdCardError, downloadFutureIdCardError]);

  if (ID_CARD_PAGE.treatment === "on") {
    return (
      <>
        <Tile
          icon={<img src={IdCard} alt="ID Card" aria-hidden="true" />}
          className={styles.tile}
          cta="ID Card"
          ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
          tagAction={{
            actionName: getIdCardTagActionName(),
            actionType: "link",
          }}
          href={policy.hasIdCard ? urls.insurance.idCardPage : undefined}
          onClick={() =>
            policy.hasIdCard ? setIsRedirecting(true) : compOnlyDialog.open()
          }
          isLoading={isRedirecting}
        />

        {compOnlyDialog.isOpen && (
          <CompOnlyDialog
            policyName={getStateName(validateState(policy.stateRegion))}
            compOnlyType={policy.compOnlyType ?? "STANDARD"}
            onClose={() => {
              mt.trackPopupClose();
              compOnlyDialog.close();
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PopoverTile
        icon={<img src={IdCard} alt="ID Card" aria-hidden="true" />}
        isLoading={isDownloadingIdCard || isDownloadingFutureIdCard}
        className={styles.tile}
        cta="ID Card"
        ariaLabel={`Access Insurance ID for your ${vehicleNamesLabel} on policy ${policyNumber}`}
        tagAction={
          showFutureDropdown
            ? undefined
            : {
                actionName: getIdCardTagActionName(),
                actionType: "button",
              }
        }
        onClick={() =>
          policy.hasIdCard ? downloadIdCard() : compOnlyDialog.open()
        }
        ctaList={
          showFutureDropdown
            ? [
                {
                  tagAction: {
                    actionName: getIdCardTagActionName(" - Current"),
                    actionType: "button",
                  },
                  onClick: downloadIdCard,
                  label: `Current: ${formatDateISO(
                    policy.effectiveDate
                  )} - ${formatDateISO(policy.expirationDate)}`,
                },
                {
                  tagAction: {
                    actionName: getIdCardTagActionName(" - Future"),
                    actionType: "button",
                  },
                  onClick: downloadFutureIdCard,
                  label: `Future: ${formatDateISO(
                    futureVersion.effectiveDate
                  )} - ${formatDateISO(futureVersion.expirationDate)}`,
                },
              ]
            : undefined
        }
      />

      {compOnlyDialog.isOpen && (
        <CompOnlyDialog
          policyName={getStateName(validateState(policy.stateRegion))}
          compOnlyType={policy.compOnlyType ?? "STANDARD"}
          onClose={() => {
            mt.trackPopupClose();
            compOnlyDialog.close();
          }}
        />
      )}

      {isErrorAlertOpen && (
        <DownloadDocumentErrorAlert
          documentName="ID Card"
          onClose={() => setIsErrorAlertOpen(false)}
        />
      )}
    </>
  );
};
